import type { FC, ReactNode } from 'react'

import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatSignedPercent } from '@/lib/format'

interface DeltaBadgeProps {
    value: number
    good?: boolean
    suffix?: string
    surface?: 'light' | 'dark'
    className?: string
}

/**
 * Change indicator. `good` decides the color (a falling expense is good),
 * the arrow shows the direction, and the text always states the number.
 */
export const DeltaBadge: FC<DeltaBadgeProps> = ({
    value,
    good,
    suffix,
    surface = 'light',
    className
}): ReactNode => {
    const positive = good ?? value >= 0
    const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight

    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                surface === 'light' && (positive ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'),
                surface === 'dark' && (positive ? 'bg-positive-soft/15 text-positive-soft' : 'bg-negative-soft/15 text-negative-soft'),
                className
            )}
        >
            <Icon aria-hidden='true' className='size-3' />
            {formatSignedPercent(value)}
            {suffix && <span className='font-normal opacity-80'>&nbsp;{suffix}</span>}
        </span>
    )
}
